<template>
  <div class="home-wrap">
    <header class="home-header">
      <button
        class="header-action"
        type="button"
        :aria-label="$t('safe.language')"
        @click="$router.push('/language')"
      >
        <img
          :src="languageIcon"
          alt=""
        >
      </button>
      <button
        class="header-action"
        type="button"
        :aria-label="$t('notice.message')"
        @click="$router.push('/notice')"
      >
        <van-icon
          name="smile-comment"
          size="26"
        />
      </button>
    </header>

    <section class="hero-section">
      <h1
        v-if="app.web_name"
        class="hero-brand"
      >
        {{ app.web_name }}
      </h1>
      <p class="hero-title">
        {{ $t('shopds.assistant') }}
      </p>
      <van-swipe
        class="hero-swiper"
        :autoplay="3000"
        lazy-render
      >
        <van-swipe-item
          v-for="(image, index) in heroImages"
          :key="index"
        >
          <img
            :src="image"
            alt=""
          >
        </van-swipe-item>
      </van-swipe>
      <button
        class="match-button"
        type="button"
        @click="$router.push('/grap')"
      >
        <span>{{ $t('shopd.graporder') }}</span>
        <van-icon name="arrow" />
      </button>
    </section>

    <main class="hotel-content">
      <section class="partners-section">
        <h2>{{ $t('item.partner') }}</h2>
        <div class="partner-list">
          <div
            v-for="(logo, index) in partnerLogos"
            :key="index"
            class="partner-logo"
          >
            <img
              :src="logo"
              alt=""
            >
          </div>
        </div>
      </section>

      <section class="rankings-section">
        <h2>{{ $t('shopds.selling') }}</h2>
        <div
          v-if="rankingsLoading"
          class="rankings-state"
        >
          <van-loading
            size="24px"
            color="#ee264d"
          />
        </div>
        <div
          v-else-if="!pros.length"
          class="rankings-state empty-state"
        >
          {{ $t('item.noData') }}
        </div>
        <div
          v-else
          class="ranking-list"
        >
          <article
            v-for="(item, index) in pros"
            :key="item.title + index"
            class="ranking-card"
          >
            <div class="ranking-cover">
              <img
                v-if="!imageErrors[index]"
                :src="resolveRankingImage(item.img)"
                :alt="item.title"
                @error="markImageError(index)"
              >
              <van-icon
                v-else
                name="photo-o"
                size="28"
              />
              <span class="ranking-index">{{ index + 1 }}</span>
            </div>
            <div class="ranking-details">
              <div class="ranking-topline">
                <h3>{{ item.title }}</h3>
                <div class="ranking-price">
                  <strong>${{ formatPrice(item.price) }}</strong><span>{{ $t('shopds.perNight') }}</span>
                </div>
              </div>
              <p class="ranking-description">
                {{ item.desc }}
              </p>
              <div class="ranking-rating">
                <van-rate
                  :value="item.rate"
                  readonly
                  allow-half
                  :size="16"
                  color="#d6b47a"
                  void-color="#e9e5df"
                />
                <span>{{ formatRate(item.rate) }}</span>
              </div>
            </div>
          </article>
        </div>
      </section>
    </main>

    <button
      class="customer-service"
      type="button"
      :aria-label="$t('webindex.chat')"
      @click="$router.push('/kefu')"
    >
      <img
        src="../images/shop2/kefu.jpg"
        alt=""
      >
    </button>

    <van-popup
      v-model="showMessage"
      round
      :style="{ lineHeight: '30px', width: '70%', padding: '15px', fontWeight: 'bold' }"
    >
      {{ message }}
    </van-popup>
    <van-dialog
      v-model="showPopup"
      width="90%"
      :confirm-button-text="$t('utils.confirm')"
    >
      <!-- eslint-disable vue/no-v-html --><!-- Existing popup content is trusted backend HTML. -->
      <div
        class="popup-content"
        v-html="popupContent"
      />
      <!-- eslint-enable vue/no-v-html -->
    </van-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import Fetch from '../../utils/fetch';
import Api from '../../interface/index';
import { Dialog, Icon, Loading, Popup, Rate, Swipe, SwipeItem } from 'vant';
import heroSunset from '../images/ban1/fe79237ab209bf72479d0dec5ef45132.png';
import heroHotel from '../images/ban1/960e5b984a99540abf2962ce49a85530.png';
import hotelThumbnail from '../images/ban1/thumbnail.png';
import hotelOverview from '../images/ban1/top.jpg';
import partnerIhg from '../images/ban1/148eecbe3781e0b0d71157c0d73ff81d.png';
import partnerHilton from '../images/ban1/1aa3e3451b96d7f5eab8fff8e107adb7.png';
import partnerWyndham from '../images/ban1/34418882bbde26552a6cab3be43fbfe0.png';
import partnerHyatt from '../images/ban1/66e4c3be38b89cf2147c7ea60bb20954.png';
import partnerAccor from '../images/ban1/7fc9e42d9882e25a46b5f998d0a636cb.png';
import partnerMarriott from '../images/ban1/99fa7a1a7c0a83e35f3b237839e1ebed.png';
import partnerCarlson from '../images/ban1/d56451459590ad6fe66e5a620db40113.png';

Vue.use(Dialog).use(Icon).use(Loading).use(Popup).use(Rate);

const LOCAL_RANKING_IMAGES = {
  'ban1:fe79237ab209bf72479d0dec5ef45132.png': heroSunset,
  'ban1:960e5b984a99540abf2962ce49a85530.png': heroHotel,
  'ban1:thumbnail.png': hotelThumbnail,
  'ban1:top.jpg': hotelOverview
};

export default {
  name: 'Index',
  components: {
    VanLoading: Loading,
    VanSwipe: Swipe,
    VanSwipeItem: SwipeItem
  },
  data() {
    return {
      lang: this.$i18n.locale || 'zh_CN',
      app: {},
      heroImages: [heroSunset, heroHotel],
      partnerLogos: [partnerIhg, partnerWyndham, partnerCarlson, partnerHilton, partnerAccor, partnerHyatt, partnerMarriott],
      localRankingImages: LOCAL_RANKING_IMAGES,
      pros: [],
      rankingsLoading: true,
      imageErrors: {},
      message: '',
      popupContent: '',
      showMessage: false,
      showPopup: false
    };
  },
  computed: {
    languageIcon() {
      return require('../images/language/' + this.lang + '.png');
    }
  },
  created() {
    this.$parent.footer('index');
  },
  mounted() {
    this.captureInviteParameters();
    this.loadHomepage();
  },
  methods: {
    captureInviteParameters() {
      const query = this.$router.history.current.query;
      if (query.agent) localStorage.setItem('agent', query.agent);
      if (query.isapp) localStorage.setItem('isapp', 'true');
    },
    loadHomepage() {
      this.loadPageConfiguration();
      this.loadRankings();
      this.loadWebConfiguration();
      this.loadUnreadNotice();
    },
    loadPageConfiguration() {
      Fetch('/index/int').then((response) => {
        const popup = (response.data || {}).popup || {};
        if (Number(popup.show) !== 1 || !popup.content) return;
        this.popupContent = popup.content;
        if (localStorage.getItem('tc') !== this.popupContent) {
          localStorage.setItem('tc', this.popupContent);
          this.showPopup = true;
        }
      });
    },
    loadRankings() {
      this.rankingsLoading = true;
      Fetch('/user/basicinfo').then((response) => {
        const data = response.data || {};
        this.pros = Array.isArray(data.pros) ? data.pros : [];
      }).catch(() => {
        this.pros = [];
      }).finally(() => {
        this.rankingsLoading = false;
      });
    },
    loadWebConfiguration() {
      Fetch('/index/webconfig').then((response) => {
        this.app = response.data || {};
      });
    },
    loadUnreadNotice() {
      Fetch('/index/sync', {}, null, false).then((response) => {
        const notices = response.data;
        if (!Array.isArray(notices) || !notices.length) return;
        this.message = notices[0].content;
        this.showMessage = true;
        setTimeout(() => Fetch('/user/notice_view', { id: notices[0].id }), 2000);
      });
    },
    resolveRankingImage(image) {
      if (this.localRankingImages[image]) return this.localRankingImages[image];
      if (/^https:\/\//i.test(image)) return image;
      if (typeof image !== 'string') return '';

      const baseUrl = this.app.domain || Api.commonUrl;
      if (!baseUrl) return '';

      return baseUrl.replace(/\/$/, '') + '/' + image.replace(/^\//, '');
    },
    markImageError(index) {
      this.$set(this.imageErrors, index, true);
    },
    formatPrice(value) {
      const price = Number(value);
      return isFinite(price) ? price.toFixed(2) : '0.00';
    },
    formatRate(value) {
      const rate = Number(value);
      return isFinite(rate) ? rate.toFixed(2) : '0.00';
    }
  }
};
</script>

<style lang="less" scoped>
@navy: #172338;
@coral: #e92c55;
@gold: #d6b47a;
@paper: #f6f4f1;
@muted: #777c85;

.home-wrap {
  min-height: 100vh;
  // Reserve space for the fixed 60px footer and its raised center action.
  padding-bottom: calc(128px + constant(safe-area-inset-bottom));
  padding-bottom: calc(128px + env(safe-area-inset-bottom));
  scroll-padding-bottom: calc(128px + env(safe-area-inset-bottom));
  // background: @paper;
  color: @navy;
}

.home-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 58px;
  padding: 0 18px;
  background: #152133;
}

.header-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, .2);
  border-radius: 50%;
  background: rgba(255, 255, 255, .08);
  color: #fff;
  cursor: pointer;
}

.header-action img {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.hero-section {
  position: relative;
  overflow: hidden;
  padding: 19px 18px 32px;
  border-radius: 0 0 26px 26px;
  background:
    radial-gradient(circle at 100% 0%, rgba(214, 180, 122, .2), transparent 36%),
    linear-gradient(155deg, #1e3046 0%, #152133 57%, #101827 100%);
}

.hero-section::before {
  position: absolute;
  top: -156px;
  right: -112px;
  width: 300px;
  height: 300px;
  border: 1px solid rgba(214, 180, 122, .17);
  border-radius: 50%;
  content: '';
  pointer-events: none;
}

.hero-section > * {
  position: relative;
  z-index: 1;
}

.hero-brand,
.hero-title {
  margin: 0;
  text-align: left;
}

.hero-brand {
  overflow: hidden;
  color: #fff;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: -.8px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hero-title {
  margin-top: 4px;
  color: #d5d9df;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
}

.hero-swiper {
  width: 100%;
  height: 55vw;
  min-height: 180px;
  max-height: 280px;
  margin-top: 21px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, .16);
  border-radius: 18px;
  background: #263344;
  box-sizing: border-box;
  box-shadow: 0 18px 32px rgba(4, 10, 18, .3);
}

.hero-swiper img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-swiper /deep/ .van-swipe__indicator {
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, .78);
  opacity: 1;
}

.hero-swiper /deep/ .van-swipe__indicator--active {
  width: 17px;
  border-radius: 4px;
  background: @coral;
}

.match-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 54px;
  margin: 22px auto 0;
  padding: 0 16px;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(105deg, #fa4268, #dc1f53);
  box-shadow: 0 11px 22px rgba(233, 44, 85, .25);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.match-button .van-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  margin-left: 12px;
  border: 1px solid rgba(255, 255, 255, .5);
  border-radius: 50%;
  font-size: 15px;
}

.hotel-content {
  min-height: 500px;
  padding: 25px 16px 12px;
}

.partners-section {
  padding: 21px 17px 22px;
  border: 1px solid #ece6e0;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(23, 35, 56, .06);
}

.partners-section h2,
.rankings-section h2 {
  margin: 0;
  color: @navy;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
}

.partners-section h2::after,
.rankings-section h2::after {
  display: block;
  width: 25px;
  height: 3px;
  margin-top: 10px;
  border-radius: 3px;
  background: @coral;
  content: '';
}

.partner-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 9px;
  margin-top: 18px;
}

.partner-logo {
  display: flex;
  flex: 0 0 calc((100% - 27px) / 4);
  align-items: center;
  justify-content: center;
  min-width: 0;
  aspect-ratio: 1;
  overflow: hidden;
  border: 1px solid #ece8e3;
  border-radius: 12px;
  background: #fff;
  box-sizing: border-box;
}

.partner-logo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.rankings-section {
  padding: 28px 0 12px;
}

.rankings-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
}

.empty-state {
  color: @muted;
  font-size: 14px;
}

.ranking-list {
  margin-top: 18px;
}

.ranking-card {
  display: flex;
  align-items: center;
  min-height: 116px;
  margin-bottom: 12px;
  padding: 10px;
  border: 1px solid #ece6e0;
  border-radius: 17px;
  background: #fff;
  box-sizing: border-box;
  box-shadow: 0 7px 20px rgba(23, 35, 56, .05);
}

.ranking-cover {
  position: relative;
  display: flex;
  flex: 0 0 96px;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  overflow: hidden;
  border-radius: 12px;
  background: #e9edf0;
  color: #9aa4ad;
}

.ranking-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ranking-index {
  position: absolute;
  bottom: 6px;
  left: 6px;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border-radius: 5px;
  background: rgba(23, 35, 56, .85);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 17px;
  text-align: center;
}

.ranking-details {
  flex: 1;
  min-width: 0;
  padding-left: 12px;
}

.ranking-topline {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.ranking-topline h3 {
  width: 100%;
  margin: 0;
  overflow: hidden;
  color: @navy;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ranking-price {
  display: flex;
  align-items: baseline;
  max-width: 100%;
  white-space: nowrap;
}

.ranking-price strong {
  color: #c52a52;
  font-size: 16px;
  line-height: 21px;
}

.ranking-price span {
  margin-left: 5px;
  color: @muted;
  font-size: 11px;
}

.ranking-description {
  margin: 5px 0 0;
  overflow: hidden;
  color: @muted;
  font-size: 12px;
  line-height: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ranking-rating {
  display: flex;
  align-items: center;
  margin-top: 7px;
}

.ranking-rating span {
  margin-left: 7px;
  color: @navy;
  font-size: 12px;
  font-weight: 700;
}

.customer-service {
  position: fixed;
  right: 14px;
  bottom: calc(128px + env(safe-area-inset-bottom));
  z-index: 3;
  width: 50px;
  height: 50px;
  padding: 0;
  overflow: hidden;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 8px 18px rgba(23, 35, 56, .18);
  cursor: pointer;
}

.customer-service img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.popup-content {
  max-height: 60vh;
  padding: 22px 20px;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.6;
}

@media (max-width: 340px) {
  .home-header {
    padding: 0 14px;
  }

  .hero-section {
    padding-right: 14px;
    padding-left: 14px;
  }

  .hotel-content {
    padding-right: 12px;
    padding-left: 12px;
  }

  .partners-section {
    padding-right: 12px;
    padding-left: 12px;
  }

  .partner-list {
    gap: 6px;
  }

  .partner-logo {
    flex-basis: calc((100% - 18px) / 4);
  }

  .ranking-cover {
    flex-basis: 82px;
    width: 82px;
    height: 90px;
  }

  .ranking-details {
    padding-left: 10px;
  }
}
</style>
